import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useApi from '../hooks/useApi';

export default function PopularPlaces() {
    const { request } = useApi();
    const [places, setPlaces] = useState([]);
    const [hoveredCard, setHoveredCard] = useState(null);
    const [contents, setContents] = useState({});
    const [cityMap, setCityMap] = useState({});
    const [townMap, setTownMap] = useState({});
    const navigate = useNavigate();

    useEffect(() => {
        const fetchPopular = async () => {
            try {
                // Şehir ve ilçe verilerini paralel olarak al
                const [citiesRes, townsRes] = await Promise.all([
                    request('/cities'),
                    request('/towns'),
                ]);

                // Şehir ve ilçe haritalarını oluştur
                const cityObj = Object.fromEntries(citiesRes.data.map(c => [c.id, c.name]));
                const townObj = Object.fromEntries(townsRes.data.map(t => [t.id, t.name]));
                setCityMap(cityObj);
                setTownMap(townObj);

                // Popüler konaklamaları getir
                const res = await request('/accommodations/city/17');
                if (res.success) {
                    setPlaces(res.data);

                    // Her konaklama için içerikleri (fotoğraflar) getir
                    const contentsPromises = res.data.map(async (place) => {
                        try {
                            const contentRes = await request(`/contents/accommodation/${place.uid}`);
                            return {
                                uid: place.uid,
                                contents: contentRes.success ? contentRes.data : [],
                            };
                        } catch (err) {
                            console.error(`İçerikler alınamadı (${place.uid}):`, err);
                            return { uid: place.uid, contents: [] };
                        }
                    });

                    // Tüm içerik isteklerini paralel olarak yap
                    const contentsResults = await Promise.all(contentsPromises);
                    const contentsMap = contentsResults.reduce((acc, item) => {
                        acc[item.uid] = item.contents;
                        return acc;
                    }, {});
                    setContents(contentsMap);
                }
            } catch (err) {
                console.error("Popüler yerler alınamadı:", err);
            }
        };
        fetchPopular();
    }, []);

    // Konaklamanın ilk IMAGE tipindeki içeriğinin URL'sini getir
    const getFirstImageUrl = (placeUid) => {
        const placeContents = contents[placeUid] || [];
        const imageContent = placeContents.find(content =>
            content.type && content.type.toUpperCase() === 'IMAGE'
        );
        return imageContent?.contentUrl || null;
    };

    return (
        <div className="px-4 py-16 bg-gradient-to-b from-blue-50 to-indigo-50">
            <div className="max-w-7xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-extrabold mb-12 bg-gradient-to-r from-blue-500 via-indigo-600 to-purple-700 text-transparent bg-clip-text drop-shadow tracking-wide text-center animate-gradient-x">
                    Çanakkale'deki Popüler Yerler
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                    {places.map((place) => {
                        const imageUrl = getFirstImageUrl(place.uid);
                        const cityName = cityMap[place.CityId] || 'Şehir bilinmiyor';
                        const townName = townMap[place.TownId] || 'İlçe bilinmiyor';

                        return (
                            <div
                                key={place.uid}
                                onClick={() => navigate(`/hostel/${place.uid}`)}
                                onMouseEnter={() => setHoveredCard(place.uid)}
                                onMouseLeave={() => setHoveredCard(null)}
                                className="cursor-pointer group rounded-2xl overflow-hidden shadow-lg bg-white hover:shadow-xl transition-all duration-500 transform hover:-translate-y-2 border border-blue-100 relative"
                            >
                                {/* Görsel Alanı */}
                                <div className="relative h-56 bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center overflow-hidden">
                                    <div className={`absolute inset-0 transition-transform duration-700 ease-out ${hoveredCard === place.uid ? 'scale-110' : 'scale-100'}`}>
                                        {imageUrl ? (
                                            <img
                                                src={imageUrl}
                                                alt={place.name}
                                                className="w-full h-full object-cover"
                                                loading="lazy"
                                            />
                                        ) : (
                                            <div className="flex flex-col items-center justify-center h-full w-full text-blue-300">
                                                <div className="relative">
                                                    <span className="text-4xl mb-2 block transition-all duration-500 group-hover:scale-110 group-hover:text-blue-400">📷</span>
                                                    <div className="absolute -inset-2 rounded-full bg-blue-100 opacity-0 group-hover:opacity-100 group-hover:animate-pulse"></div>
                                                </div>
                                                <span className="text-sm font-medium text-blue-400 group-hover:text-blue-600 transition-colors">Fotoğraf Yok</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Overlay animasyonu */}
                                    <div className="absolute inset-0 bg-gradient-to-t from-blue-900/40 via-blue-900/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                                    {/* Detay butonu */}
                                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                                        <span className="inline-flex items-center px-4 py-2 bg-white text-blue-600 text-sm font-semibold rounded-full shadow-md hover:bg-blue-50 transition-colors">
                                            Detayları Gör
                                            <svg className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
                                            </svg>
                                        </span>
                                    </div>

                                    {/* Fiyat etiketi */}
                                    {place.price && (
                                        <div className={`absolute top-4 right-4 px-3 py-1.5 bg-white text-blue-600 font-bold rounded-full shadow-md transition-all duration-300 transform ${hoveredCard === place.uid ? 'scale-110 rotate-6' : 'scale-100 rotate-0'}`}>
                                            ₺{place.price}
                                        </div>
                                    )}
                                </div>

                                {/* Bilgi Alanı */}
                                <div className="p-5 space-y-2 relative overflow-hidden">
                                    <div className="transition-transform duration-500 transform group-hover:-translate-y-2">
                                        <div className="flex justify-between items-start">
                                            <h4 className="text-lg font-bold text-gray-800 group-hover:text-blue-600 transition-colors line-clamp-1">
                                                {place.name}
                                            </h4>
                                        </div>

                                        <p className="text-sm text-indigo-600 font-medium transition-colors group-hover:text-blue-400">
                                            {cityName} / {townName}
                                        </p>

                                        <p className="text-sm text-gray-600 line-clamp-2 transition-colors group-hover:text-gray-700">
                                            {place.description || 'Açıklama bulunmamaktadır'}
                                        </p>
                                    </div>

                                    {/* Favori butonu */}
                                    <div className="pt-3 flex justify-between items-center opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-100">
                                        <button className="p-1 text-blue-400 hover:text-blue-600 transition-colors animate-pulse hover:animate-none">
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                            </svg>
                                        </button>
                                    </div>

                                    {/* Alt çizgi efekti */}
                                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-400 to-indigo-500 transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-1000"></div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}